import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { environment } from '@enviroment/environment';

const RTIR_ENABLED_ENVIRONMENTS = new Set(['test', 'development']);

/**
 * Allowlist de endpoints donde sí aplica `rtir` + headers QA.
 * Para habilitar otro servicio, agregar una entrada aquí.
 */
const RTIR_QA_ENDPOINT_ALLOWLIST: ReadonlyArray<{
  service: string;
  matches: (pathname: string) => boolean;
}> = [
  {
    service: 'SimulateInvestmentService',
    matches: (pathname) => pathname.endsWith('/simulate-investment'),
  },
  {
    service: 'SimulateInvestmentV2Service',
    matches: (pathname) => pathname.includes('/simulate-investment/simulate-investment-v2'),
  },
  {
    service: 'LoginService',
    matches: (pathname) => pathname.includes('/auth/sign-up') || pathname.includes('/auth/sign-in'),
  },
  {
    service: 'PqrService',
    matches: (pathname) => pathname.endsWith('/users/pqr/public'),
  },
];

/**
 * Cuando la URL del navegador incluye `?rtir=<HAS_UUID_REMOVE_RTIR>` en test/local,
 * propaga ese query param y headers QA solo a los endpoints de la allowlist.
 */
export const rtirQaInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  const document = inject(DOCUMENT);

  if (!shouldApplyRtirQaHeaders(platformId, document.defaultView?.location?.href ?? '')) {
    return next(req);
  }

  if (!isAllowlistedApiRequest(req.url)) {
    return next(req);
  }

  const requestUrl = new URL(req.url, environment.API_BASE_URL);
  requestUrl.searchParams.set('rtir', environment.HAS_UUID_REMOVE_RTIR);

  return next(
    req.clone({
      url: `${requestUrl.origin}${requestUrl.pathname}${requestUrl.search}${requestUrl.hash}`,
      setHeaders: {
        'x-key': environment.QA_AUTOMATION_X_KEY,
        'X-QA-Automation-Key': environment.QA_AUTOMATION_KEY,
      },
    }),
  );
};

function shouldApplyRtirQaHeaders(platformId: Object, currentHref: string): boolean {
  if (!isPlatformBrowser(platformId) || environment.production) {
    return false;
  }

  if (!RTIR_ENABLED_ENVIRONMENTS.has(environment.ENVIRONMENT_NAME)) {
    return false;
  }

  if (!environment.QA_AUTOMATION_X_KEY || !environment.QA_AUTOMATION_KEY) {
    return false;
  }

  const queryString = currentHref.split('?')[1] ?? '';
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get('rtir') === environment.HAS_UUID_REMOVE_RTIR;
}

function isAllowlistedApiRequest(requestUrl: string): boolean {
  try {
    const apiBase = new URL(environment.API_BASE_URL);
    const absoluteRequestUrl = new URL(requestUrl, environment.API_BASE_URL);

    if (absoluteRequestUrl.origin !== apiBase.origin) {
      return false;
    }

    return RTIR_QA_ENDPOINT_ALLOWLIST.some((endpoint) => endpoint.matches(absoluteRequestUrl.pathname));
  } catch {
    return false;
  }
}
